import { trackApiClient } from '@/clients/TrackApiClient';
import { TranslatableColDef } from '@/components/common/Grid/TranslatableColDef';
import { trackListColDef } from '../utils/TrackListColDef';
import useGroupedProducts from '@/components/partials/ProductTabs/GroupedProducts/useGroupedProducts';
import {
  Delete,
  Duplicate,
  Edit,
  GetTrackFromDigital,
  GetTrackFromPhysical,
} from '@/constants/Operations';
import { ITrack, TrackType } from '@/data/models/track';
import { IValidationResults, ValidationLevel } from '@/data/models/validation';
import { useTranslatedGridLocaleText } from '@/hooks/useTranslatedGridLocaleText';
import { formatSideLetter, isVideoFormat } from '@/utils/helper';
import _, { cloneDeep, filter, includes, isEmpty, map } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GetRowIdParams, RowSelectionOptions } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { IAlbum } from '@/data/models/album';
import { parseAddMultipleTracksQuantity } from '@/utils/trackQuantityUtils';
import { isValidationError } from '@/utils/validationErrors';

const DEFAULT_TAB_INDEX = 0;

interface UseProductTabsProps {
  tracks?: ITrack[];
  album: IAlbum;
  reloadTracks?: () => Promise<void> | void;
  errors: IValidationResults[];
  usesSides?: boolean;
  toggleTrackPopUp?: (track: ITrack) => void;
  toggleMultipleTracksPopUp?: (tracks: ITrack[], areNewTracks: boolean) => void;
  trackSelectionResetSignal?: number;
}

export const useProductTabs = ({
  tracks = [],
  album,
  reloadTracks = () => {
    return;
  },
  errors,
  usesSides,
  toggleTrackPopUp = () => {
    return;
  },
  toggleMultipleTracksPopUp = () => {
    return;
  },
  trackSelectionResetSignal,
}: UseProductTabsProps) => {
  const { t } = useTranslation();
  const [tabIndex, setTabIndex] = useState(DEFAULT_TAB_INDEX);
  const [showPopUp, setShowPopup] = useState(false);
  const [volumeNumbers, setVolumeNumbers] = useState<number[]>([]);
  const [isValidRestrictions, setIsValidRestrictions] = useState(true);
  const [showReorderTracks, setShowReorderTracks] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationModalBody, setConfirmationModalBody] = useState('');
  const [confirmationModalHeader, setConfirmationModalHeader] = useState('');
  const [confirmationModalAction, setConfirmationModalAction] = useState<
    () => Promise<void>
  >(() => Promise.resolve());
  const [showMoveTracksToVolumeModal, setShowMoveTracksToVolumeModal] =
    useState(false);
  const [showCopyProductMetadataModal, setShowCopyProductMetadataModal] =
    useState(false);
  const [showLookupTrackModal, setShowLookupTrackModal] = useState(false);
  const [lookupSource, setLookupSource] = useState<'digital' | 'physical'>(
    'physical'
  );
  const [lookupTrack, setLookupTrack] = useState<ITrack | null>(null);
  const [
    showMoveTracksToNewVolumeConfirmationModal,
    setShowMoveTracksToNewVolumeConfirmationModal,
  ] = useState(false);
  const defaultTrackType = isVideoFormat(album.format?.name)
    ? TrackType.VideoAndAudio
    : null;

  const emptyTrackTemplate: ITrack = {
    albumId: '',
    artists: [],
    audioLanguage: '',
    copyrightLine: '',
    copyrightYear: 0,
    discOrSideNumber: 0,
    displayArtist: '',
    duration: 0,
    genre: undefined,
    isrc: '',
    phonographicCopyrightLine: '',
    phonographicCopyrightYear: 0,
    publishers: [],
    title: '',
    trackType: defaultTrackType,
    trackNumber: 0,
    version: '',
    writersComposers: [],
  };

  const groupedProducts = useGroupedProducts();
  const { getLocaleText } = useTranslatedGridLocaleText();

  useEffect(() => {
    buildVolumes(tracks);
  }, []);

  useEffect(() => {
    setTabIndex(DEFAULT_TAB_INDEX);
  }, [album.id]);

  useEffect(() => {
    setIsValidRestrictions(
      !errors.some(
        (e) =>
          e.validationLevel === ValidationLevel.Restrictions &&
          isValidationError(e)
      )
    );
  }, [errors]);

  function buildVolumes(tracks: ITrack[]) {
    if (tracks.length) {
      const maxVolumeNumber = tracks.reduce((prev, curr) =>
        curr.discOrSideNumber > prev.discOrSideNumber ? curr : prev
      )?.discOrSideNumber;
      if (!maxVolumeNumber) {
        setVolumeNumbers([1]);
      } else {
        const volumes = [];
        for (let i = 1; i <= maxVolumeNumber; i++) {
          volumes.push(i);
        }
        setVolumeNumbers(volumes);
      }
    } else {
      setVolumeNumbers([1]);
    }
  }

  const addNewTrack = (discOrSideNumber: number) => {
    setShowPopup(!showPopUp);
    const track: ITrack = {
      isrc: '',
      trackNumber:
        tracks
          .filter((t) => t.discOrSideNumber === discOrSideNumber)
          .map((t) => t.trackNumber)
          .reduce((curr, prev) => (curr > prev ? curr : prev), 0) + 1,
      title: '',
      version: '',
      displayArtist: '',
      discOrSideNumber: discOrSideNumber,
      id: 0,
      albumId: album.id,
      artists: [],
      trackType: defaultTrackType,
    };
    toggleTrackPopUp(track);
  };

  const removeVolume = async (volumeToBeRemoved: number) => {
    if (volumeToBeRemoved && album.id) {
      await trackApiClient.DeleteVolume(album.id, volumeToBeRemoved);
      await reloadTracks();
      if (volumeNumbers.length > 1) {
        setVolumeNumbers(volumeNumbers.slice(0, -1));
      }
    }
  };

  const AddVolume = async (option: string, volumeNumber: number) => {
    if (option === 'add') {
      const lastNumber = volumeNumbers[volumeNumbers.length - 1];
      setVolumeNumbers([...volumeNumbers, lastNumber + 1]);
    }
    if (option === 'remove') {
      setConfirmationModalHeader(
        t('product.productTabs.confirmRemoveVolumeHeader')
      );
      setConfirmationModalBody(
        t('product.productTabs.confirmRemoveVolumeBody', {
          trackNames: tracks
            .filter((t) => t.discOrSideNumber === volumeNumber)
            .map((t) => (t.title ? t.title : 'Untitled'))
            .join(', '),
        })
      );
      setConfirmationModalAction(
        () => async () => await removeVolume(volumeNumber)
      );
      setShowConfirmationModal(true);
    }
  };

  const removeTrack = async (trackId: number) => {
    await trackApiClient.DeleteTrack(trackId);
    await reloadTracks();
  };

  async function addRemoveTrack(track: ITrack, option: string) {
    if (option === Delete) {
      setConfirmationModalHeader(
        t('product.productTabs.confirmRemoveTrackHeader')
      );
      setConfirmationModalBody(t('product.productTabs.confirmRemoveTrackBody'));
      setConfirmationModalAction(
        () => async () => await removeTrack(track.id!)
      );
      setShowConfirmationModal(true);
    }
    if (option === Edit) {
      toggleTrackPopUp(track);
    }
    if (option === Duplicate) {
      const duplicateTrack = _.clone(track);
      duplicateTrack.id = undefined;
      duplicateTrack.isrc = '';
      duplicateTrack.trackType =
        defaultTrackType === null
          ? null
          : duplicateTrack.trackType ?? defaultTrackType;
      duplicateTrack.trackNumber =
        tracks
          .filter((t) => t.discOrSideNumber === track.discOrSideNumber)
          .map((t) => t.trackNumber)
          .reduce((prev, curr) => (prev > curr ? prev : curr), 0) + 1;
      await trackApiClient.createTrack(duplicateTrack);
      await reloadTracks();
    }
    if (option === GetTrackFromDigital) {
      setLookupSource('digital');
      setLookupTrack(track);
      setShowLookupTrackModal(true);
    }
    if (option === GetTrackFromPhysical) {
      setLookupSource('physical');
      setLookupTrack(track);
      setShowLookupTrackModal(true);
    }
  }

  const formatHeader = (i: number): string => {
    if (usesSides) {
      const letter = formatSideLetter(i);
      return t('product.editProduct.sideLabel', { letter });
    } else {
      return t('product.editProduct.discLabel', { i });
    }
  };

  const getColumnDefs = () => {
    const columnDefs: TranslatableColDef[] = [];
    trackListColDef.forEach((tlc) => {
      const columnDef = cloneDeep(tlc);
      columnDef.headerName = tlc?.headerNameTranslationKey
        ? t(tlc?.headerNameTranslationKey)
        : tlc?.headerName;
      delete columnDef.headerNameTranslationKey;
      columnDefs.push(columnDef);
    });
    return columnDefs;
  };

  const finalColumnDefs = getColumnDefs();

  const [showAddMultipleTracks, setShowAddMultipleTracks] = useState(false);
  const [addMultipleTracksDiskNumber, setAddMultipleTracksDiskNumber] =
    useState(0);
  const [showEditMultipleTracks, setShowEditMultipleTracks] = useState(false);
  const [multipleTracksList, setMultipleTracksList] = useState<ITrack[]>([]);

  const addMultipleTracks = (discOrSideNumber: number) => {
    setAddMultipleTracksDiskNumber(discOrSideNumber);
    setShowAddMultipleTracks(true);
  };

  const handleAddMultipleTracks = async (tracksNumber: string) => {
    const tracksQuantity = parseAddMultipleTracksQuantity(tracksNumber);
    if (tracksQuantity === null) {
      return;
    }

    const addedTracks: ITrack[] = [];
    for (let i = 0; i < tracksQuantity; i++) {
      const trackNumber =
        tracks.filter((t) => t.discOrSideNumber === addMultipleTracksDiskNumber)
          .length +
        (i + 1);
      addedTracks.push({
        albumId: album.id,
        isrc: '',
        trackNumber: trackNumber,
        title: '',
        version: '',
        displayArtist: '',
        discOrSideNumber: addMultipleTracksDiskNumber,
        trackType: defaultTrackType,
      });
    }
    let result: ITrack[] = [];
    await trackApiClient
      .createTracks(addedTracks)
      .then((createdTracks: any) => {
        setMultipleTracksList(createdTracks?.data);
        result = createdTracks?.data;
      })
      .catch((reason) => {
        console.error('Error on create multiple tracks', reason);
        setShowAddMultipleTracks(false);
      })
      .finally(async () => {
        await reloadTracks();
        setShowAddMultipleTracks(false);
        setShowEditMultipleTracks(true);
        toggleMultipleTracksPopUp(result, true);
      });
  };

  const [selectedTracks, setSelectedTracks] = useState<ITrack[]>([]);
  const [selectedTracksIds, setSelectedTracksIds] = useState<number[]>([]);
  const [isSelectedAllChecked, setIsSelectedAllChecked] = useState(false);
  const [isSelectedAllIntermediate, setIsSelectedAllIntermediate] =
    useState(false);

  const rowSelection = useMemo(
    ():
      | 'multiple'
      | 'single'
      | RowSelectionOptions<any, any, any>
      | undefined => ({
      mode: 'multiRow',
      groupSelects: 'descendants',
    }),
    []
  );
  const getRowId = useCallback(
    (params: GetRowIdParams) => String(params.data.id),
    []
  );
  const gridsRef = useRef<Map<string, AgGridReact>>(new Map());

  const onSelectionChanged = useCallback(() => {
    const selectedRows: any[] = [];

    gridsRef.current.forEach((gridInstance) => {
      const api = gridInstance.api;
      if (api) {
        selectedRows.push(...api.getSelectedRows());
      }
    });

    if (isSelectedAllChecked) {
      if (selectedRows.length > 0 && selectedRows.length !== tracks.length) {
        setIsSelectedAllIntermediate(true);
        setIsSelectedAllChecked(false);
      } else if (selectedRows.length === tracks.length) {
        setIsSelectedAllIntermediate(false);
      } else if (selectedRows.length === 0) {
        setIsSelectedAllChecked(false);
        setIsSelectedAllIntermediate(false);
      }
    } else if (!isSelectedAllChecked) {
      if (selectedRows.length > 0 && selectedRows.length !== tracks.length) {
        setIsSelectedAllIntermediate(true);
      } else if (selectedRows.length === tracks.length) {
        setIsSelectedAllChecked(true);
        setIsSelectedAllIntermediate(false);
      } else if (selectedRows.length === 0 && isSelectedAllIntermediate) {
        setIsSelectedAllIntermediate(false);
      }
    } else {
      setIsSelectedAllIntermediate(false);
    }

    getSelectedTracksIDs(selectedRows);
    setSelectedTracks(selectedRows);
  }, [isSelectedAllChecked, isSelectedAllIntermediate]);

  const getSelectedTracksIDs = (array: any) => {
    setSelectedTracksIds(map(array, 'id'));
  };

  const handleSelectAllTracks = () => {
    if (!isSelectedAllChecked) {
      handleSelectAll();
    } else {
      handleDeselectAll();
    }
  };

  const handleSelectAll = () => {
    setIsSelectedAllChecked(true);
    setIsSelectedAllIntermediate(false);
    gridsRef.current.forEach((gridInstance) => {
      gridInstance?.api.selectAll();
    });
  };

  const handleDeselectAll = useCallback(() => {
    setIsSelectedAllChecked(false);
    setIsSelectedAllIntermediate(false);
    setSelectedTracks([]);
    setSelectedTracksIds([]);
    gridsRef.current.forEach((gridInstance) => {
      gridInstance?.api?.deselectAll();
    });
  }, []);

  useEffect(() => {
    if (!trackSelectionResetSignal) return;
    handleDeselectAll();
  }, [trackSelectionResetSignal, handleDeselectAll]);

  const editSelectedTracks = async () => {
    const tracksToEdit = filter(tracks, (t: ITrack) =>
      includes(selectedTracksIds, t.id)
    );
    if (tracksToEdit.length === 1) {
      toggleTrackPopUp(tracksToEdit[0]);
    } else {
      setShowEditMultipleTracks(true);
      toggleMultipleTracksPopUp(tracksToEdit, false);
    }
  };

  const deleteSelectedTracks = async () => {
    setConfirmationModalHeader(
      t('product.productTabs.confirmRemoveTracksHeader')
    );
    let tracksTitle = '';
    map(
      selectedTracks,
      (st) =>
        (tracksTitle += !isEmpty(st.title)
          ? `${st.title}, `
          : `<${t('common.untitled')}>, `)
    );
    tracksTitle = tracksTitle.slice(0, -1) + '?';
    setConfirmationModalBody(
      `${t('product.productTabs.confirmRemoveTracksBody')} ${tracksTitle}`
    );
    setConfirmationModalAction(() => async () => {
      const tracksIdList = map(selectedTracks, (t: ITrack) => t.id!);
      await trackApiClient.DeleteTracks(tracksIdList);
      await reloadTracks();
    });
    setShowConfirmationModal(true);
  };

  const handleAddToVolumeClick = () => {
    if (volumeNumbers.length === 1) {
      setShowMoveTracksToNewVolumeConfirmationModal(true);
    } else {
      setShowMoveTracksToVolumeModal(true);
    }
  };

  const handleCopyProductMetadataClick = () => {
    setShowCopyProductMetadataModal(true);
  };

  return {
    tabIndex,
    setTabIndex,
    showPopUp,
    volumeNumbers,
    isValidRestrictions,
    showReorderTracks,
    setShowReorderTracks,
    showConfirmationModal,
    setShowConfirmationModal,
    confirmationModalBody,
    confirmationModalHeader,
    confirmationModalAction,
    groupedProducts,
    getLocaleText,
    addNewTrack,
    addMultipleTracks,
    handleAddMultipleTracks,
    showAddMultipleTracks,
    setShowAddMultipleTracks,
    showEditMultipleTracks,
    setShowEditMultipleTracks,
    multipleTracksList,
    setMultipleTracksList,
    emptyTrackTemplate,
    AddVolume,
    addRemoveTrack,
    formatHeader,
    finalColumnDefs,
    isSelectedAllChecked,
    isSelectedAllIntermediate,
    handleSelectAllTracks,
    deleteSelectedTracks,
    editSelectedTracks,
    onSelectionChanged,
    rowSelection,
    getRowId,
    gridsRef,
    selectedTracks,
    showMoveTracksToVolumeModal,
    setShowMoveTracksToVolumeModal,
    showCopyProductMetadataModal,
    setShowCopyProductMetadataModal,
    showLookupTrackModal,
    setShowLookupTrackModal,
    lookupSource,
    lookupTrack,
    handleAddToVolumeClick,
    handleCopyProductMetadataClick,
    showMoveTracksToNewVolumeConfirmationModal,
    setShowMoveTracksToNewVolumeConfirmationModal,
  };
};
